import { Parser } from '~/api';
import { SentEmail } from '~/api/models';
import RequestService from './request';

export interface EmailTemplateVariant {
    locale: string;
    enabled: boolean;
    use_layout?: boolean;
    subject?: string | null;
    body?: string | null;
    updated_at?: string | null;
}

export interface EmailTemplateListEntry {
    key: string;
    has_subject: boolean;
    variables: Record<string, string[]>;
    customized: boolean;
    variants: EmailTemplateVariant[];
}

export interface EmailTemplateDetail {
    key: string;
    has_subject: boolean;
    variables: Record<string, string[]>;
    default_subject: string;
    default_body: string;
    variants: EmailTemplateVariant[];
}

export interface EmailTemplatePayload {
    locale?: string;
    enabled?: boolean;
    use_layout?: boolean;
    subject?: string | null;
    body?: string | null;
}

export interface EmailTemplateVersionEntry {
    id: string;
    locale: string;
    enabled: boolean;
    use_layout: boolean;
    subject: string | null;
    body: string | null;
    created_at: string;
}

export interface EmailTemplatePreview {
    subject: string;
    html: string;
    customized: boolean;
}

class EmailsService {
    getTemplates(): Promise<EmailTemplateListEntry[]> {
        return RequestService.get('/emails/templates').then(({ data }) => data);
    }

    getTemplate(key: string): Promise<EmailTemplateDetail> {
        return RequestService.get(`/emails/templates/${key}`);
    }

    updateTemplate(key: string, data: EmailTemplatePayload) {
        return RequestService.put(`/emails/templates/${key}`, data);
    }

    deleteTemplate(key: string, locale: string) {
        return RequestService.delete(`/emails/templates/${key}?locale=${encodeURIComponent(locale)}`);
    }

    previewTemplate(key: string, data: { template?: EmailTemplatePayload; locale?: string }): Promise<EmailTemplatePreview> {
        return RequestService.post(`/emails/templates/${key}/preview`, data);
    }

    testTemplate(key: string, data: { template?: EmailTemplatePayload; locale?: string }) {
        return RequestService.post(`/emails/templates/${key}/test`, data);
    }

    getTemplateVersions(key: string): Promise<EmailTemplateVersionEntry[]> {
        return RequestService.get(`/emails/templates/${key}/versions`).then(({ data }) => data);
    }

    restoreTemplateVersion(key: string, versionId: string) {
        return RequestService.post(`/emails/templates/${key}/versions/${versionId}/restore`);
    }

    getAll(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/emails', {
            ...req,
            sort: req.sort ?? '-created_at',
            include: ['user'],
        }).then(Parser.parse);
    }

    get(id: string): Promise<SentEmail> {
        return RequestService.get(`/emails/${id}`, { include: ['user'] }).then(Parser.parse);
    }

    getForUser(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/users/:user/emails', {
            ...req,
            sort: req.sort ?? '-created_at',
        }).then(Parser.parse);
    }
}

export default new EmailsService();
